import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { X, Upload, Download, Sparkles, CheckCircle2, AlertTriangle, History, RotateCcw } from "lucide-react";

const DEAL_COLUMNS = [
  "client", "property", "deal_type", "stage", "source",
  "expected_close_date", "price", "commission_pct", "closed",
  "closed_date", "next_follow_up", "priority", "notes",
];
const DEAL_EXAMPLE = {
  client: "Acme Corp", property: "Website Redesign", deal_type: "New Business",
  stage: "Proposal", source: "Referral", expected_close_date: "2026-07-15",
  price: "12000", commission_pct: "10", closed: "no", closed_date: "",
  next_follow_up: "2026-06-20", priority: "High", notes: "Follow up after demo",
};

const csvEscape = (v) => {
  if (v == null) return "";
  const s = String(v).replace(/"/g, '""');
  return /[",\n]/.test(s) ? `"${s}"` : s;
};

const downloadCsv = (filename, headers, exampleRow) => {
  const lines = [headers.map(csvEscape).join(",")];
  if (exampleRow) lines.push(headers.map((h) => csvEscape(exampleRow[h])).join(","));
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export default function ImportModal({ target, tab, onClose, onImported }) {
  const fileRef = useRef(null);
  const [rows, setRows] = useState(null);
  const [columns, setColumns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [done, setDone] = useState(0);
  const [skipDupes, setSkipDupes] = useState(true);
  const [aiProgress, setAiProgress] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [histBusy, setHistBusy] = useState(false);
  const [sourceHeaders, setSourceHeaders] = useState([]);
  const [sourceRows, setSourceRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [showMap, setShowMap] = useState(false);

  const isTab = target === "tab";
  const label = isTab ? tab?.label || "Tab" : "Pipeline";

  const templateHeaders = useMemo(() => {
    if (!isTab) return DEAL_COLUMNS;
    if (tab?.type === "tracker") {
      const svc = (tab.services || []).map((s) => s.name).filter(Boolean);
      return ["client", tab.subject_label || "subject", "date", "time",
        ...svc, "scheduled", "paid", "done", "notes"];
    }
    return ["done", "title", "date", "amount", "notes"];
  }, [isTab, tab]);

  const templateExample = useMemo(() => {
    if (!isTab) return DEAL_EXAMPLE;
    if (tab?.type === "tracker") {
      const ex = { client: "Acme Corp", [tab.subject_label || "subject"]: "Downtown Office",
        date: "2026-07-15", time: "10:30", scheduled: "yes", paid: "no", done: "no",
        notes: "Bring samples" };
      (tab.services || []).forEach((s, i) => { ex[s.name] = i === 0 ? "yes" : "no"; });
      return ex;
    }
    return { done: "no", title: "Renew software license", date: "2026-07-01",
      amount: "499", notes: "Annual" };
  }, [isTab, tab]);

  const onTemplate = () => {
    const fname = (isTab ? label : "pipeline").toLowerCase().replace(/\s+/g, "-");
    downloadCsv(`${fname}-import-template.csv`, templateHeaders, templateExample);
  };

  const parseUrl = isTab ? `/import/tabs/${tab.id}/parse` : "/import/deals/parse";
  const commitUrl = isTab ? `/import/tabs/${tab.id}/commit` : "/import/deals/commit";
  const historyParams = isTab ? { tab_id: tab.id } : { kind: "deals" };

  const loadHistory = async () => {
    setHistBusy(true);
    try {
      const res = await api.get("/import/history", { params: historyParams }).then((r) => r.data);
      setHistory(Array.isArray(res) ? res : []);
    } catch (_) { /* noop */ } finally {
      setHistBusy(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadHistory(); }, []);

  const onUndo = async (batchId) => {
    if (!window.confirm("Undo this import? The rows it added will be deleted.")) return;
    setHistBusy(true);
    try {
      await api.post(`/import/undo/${batchId}`);
      await loadHistory();
      await onImported?.();
    } catch (err) {
      setMsg(err?.response?.data?.detail || "Undo failed — try again.");
    } finally {
      setHistBusy(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(parseUrl, fd).then((r) => r.data);
      setRows(res.rows || []);
      setColumns(res.columns || templateHeaders);
      setSourceHeaders(res.source_headers || []);
      setSourceRows((res.rows || []).map((r) => r.raw));
      setMapping(res.auto_mapping || {});
      // Auto-open the mapping panel when the CSV headers don't line up.
      const cols = res.columns || templateHeaders;
      const matched = Object.keys(res.auto_mapping || {}).length;
      setShowMap(matched < Math.ceil(cols.length / 2));
    } catch (err) {
      setMsg(err?.response?.data?.detail || "Could not read that CSV.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const applyMapping = async () => {
    if (!sourceRows.length) return;
    setBusy(true);
    setMsg("");
    try {
      const res = await api.post("/import/remap", {
        target,
        tab_id: isTab ? tab.id : null,
        rows: sourceRows,
        mapping,
      }).then((r) => r.data);
      setRows(res.rows || []);
      setColumns(res.columns || columns);
      setShowMap(false);
    } catch (err) {
      setMsg(err?.response?.data?.detail || "Could not apply mapping.");
    } finally {
      setBusy(false);
    }
  };

  const errorCount = (rows || []).filter((r) => r.status === "error").length;
  const dupCount = (rows || []).filter((r) => r.status === "ok" && r.duplicate).length;
  const importable = (rows || []).filter(
    (r) => r.status === "ok" && !(skipDupes && r.duplicate),
  );
  const okCount = importable.length;

  // Bulk AI cleanup — repair every flagged row, in chunks so large files
  // don't blow the LLM token limit or the request timeout. Progress is shown.
  const onAiFix = async () => {
    if (!rows) return;
    const errorRows = rows.filter((r) => r.status === "error");
    if (errorRows.length === 0) return;
    setBusy(true);
    setMsg("");
    setAiProgress({ done: 0, total: errorRows.length });
    const CHUNK = 25;
    const fixedByIndex = {};
    try {
      for (let i = 0; i < errorRows.length; i += CHUNK) {
        const chunk = errorRows.slice(i, i + CHUNK);
        const res = await api.post("/import/ai-fix", {
          target,
          tab_id: isTab ? tab.id : null,
          rows: chunk.map((r) => r.raw),
        }, { timeout: 90000 }).then((r) => r.data);
        const fixed = res.rows || [];
        chunk.forEach((r, j) => { if (fixed[j]) fixedByIndex[r.index] = fixed[j]; });
        setAiProgress({ done: Math.min(i + CHUNK, errorRows.length), total: errorRows.length });
      }
      setRows((prev) => prev.map((r) => {
        const f = fixedByIndex[r.index];
        return f ? { ...f, index: r.index } : r;
      }));
    } catch (err) {
      setMsg(err?.response?.data?.detail || "AI repair failed — try again.");
    } finally {
      setBusy(false);
      setAiProgress(null);
    }
  };

  const onCommit = async () => {
    if (!rows) return;
    const good = importable.map((r) => r.mapped);
    if (good.length === 0) return;
    setBusy(true);
    setMsg("");
    try {
      const res = await api.post(commitUrl, { rows: good }).then((r) => r.data);
      setDone(res.created || good.length);
      await onImported?.();
      loadHistory();
    } catch (err) {
      setMsg(err?.response?.data?.detail || "Import failed — try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/65 backdrop-blur-sm flex items-start justify-center pt-10 px-4 animate-fadein"
      onClick={onClose}
      data-testid="import-modal"
    >
      <div
        className="hud-frame w-full max-w-4xl max-h-[86vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ background: "hsl(var(--popover))" }}
      >
        <div className="tread-plate h-1.5" />
        <div className="p-6 space-y-5">
          <div className="flex items-start justify-between">
            <div>
              <div className="kpi-label">Import CSV</div>
              <h2 className="display text-2xl font-bold uppercase mt-1">{label}</h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowHistory((s) => !s)}
                className={`hud-btn !px-2 !py-1 flex items-center gap-1 ${showHistory ? "hud-btn-primary" : ""}`}
                data-testid="import-history-toggle"
                title="Import history"
              >
                <History size={11} /> HISTORY
              </button>
              <button onClick={onClose} className="hud-btn !px-2 !py-1" data-testid="import-close">
                <X size={11} />
              </button>
            </div>
          </div>

          {showHistory && (
            <div className="hud-frame p-4 space-y-3" data-testid="import-history">
              <div className="kpi-label">Recent imports</div>
              {histBusy && history.length === 0 ? (
                <div className="mono text-[10px] text-muted-foreground tracking-widest uppercase">Loading…</div>
              ) : history.length === 0 ? (
                <div className="mono text-[10px] text-muted-foreground tracking-widest uppercase">
                  No imports yet.
                </div>
              ) : (
                history.map((h) => (
                  <div key={h.id} className="flex items-center justify-between gap-3 border-b border-border pb-2 last:border-0"
                    data-testid={`history-${h.id}`}>
                    <div className="text-xs">
                      <span className="font-semibold text-[hsl(var(--oxblood))]">
                        {h.count} {h.count === 1 ? "row" : "rows"}
                      </span>
                      <span className="text-muted-foreground"> · {h.label} · {new Date(h.created_at).toLocaleString()}</span>
                    </div>
                    <button
                      onClick={() => onUndo(h.id)}
                      disabled={histBusy}
                      className="hud-btn !px-2 !py-1 flex items-center gap-1 !text-[hsl(var(--oxblood))]"
                      data-testid={`history-undo-${h.id}`}
                    >
                      <RotateCcw size={11} /> UNDO
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          {done > 0 ? (
            <div className="text-center py-10 space-y-4" data-testid="import-success">
              <CheckCircle2 size={40} className="mx-auto text-[hsl(var(--oxblood))]" />
              <div className="display text-xl font-bold uppercase">
                {done} {done === 1 ? "row" : "rows"} imported
              </div>
              <button onClick={onClose} className="hud-btn hud-btn-primary" data-testid="import-done">
                DONE
              </button>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <button onClick={onTemplate} className="hud-btn flex items-center gap-2" data-testid="import-template">
                  <Download size={12} /> DOWNLOAD TEMPLATE
                </button>
                <button
                  onClick={() => fileRef.current?.click()}
                  disabled={busy}
                  className="hud-btn hud-btn-primary flex items-center gap-2"
                  data-testid="import-upload"
                >
                  <Upload size={12} /> {busy && !rows ? "READING…" : "UPLOAD CSV"}
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={onFile}
                  className="hidden"
                  data-testid="import-file-input"
                />
                <p className="mono text-[10px] text-muted-foreground tracking-widest uppercase">
                  Fill the template, then upload it here.
                </p>
              </div>

              {msg && (
                <div className="hud-frame p-3 text-sm text-[hsl(var(--oxblood))]" data-testid="import-error">
                  {msg}
                </div>
              )}

              {rows && (
                <div className="space-y-4" data-testid="import-preview">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="mono text-xs tracking-widest uppercase text-muted-foreground">
                      <span className="text-[hsl(var(--oxblood))]">{okCount} ready</span>
                      {errorCount > 0 && <span> · {errorCount} need fixing</span>}
                      {dupCount > 0 && <span className="text-amber-500"> · {dupCount} duplicate{dupCount === 1 ? "" : "s"}</span>}
                      <span> · {rows.length} total</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {dupCount > 0 && (
                        <label className="flex items-center gap-1.5 mono text-[10px] tracking-widest uppercase text-muted-foreground cursor-pointer select-none"
                          data-testid="import-skip-dupes">
                          <input
                            type="checkbox"
                            checked={skipDupes}
                            onChange={(e) => setSkipDupes(e.target.checked)}
                            className="accent-[hsl(var(--oxblood))]"
                          />
                          Skip duplicates
                        </label>
                      )}
                      {errorCount > 0 && (
                        <button
                          onClick={onAiFix}
                          disabled={busy}
                          className="hud-btn flex items-center gap-2"
                          data-testid="import-ai-fix"
                        >
                          <Sparkles size={12} />
                          {aiProgress
                            ? `FIXING ${aiProgress.done}/${aiProgress.total}…`
                            : `FIX ALL ${errorCount} WITH AI`}
                        </button>
                      )}
                      <button
                        onClick={onCommit}
                        disabled={busy || okCount === 0}
                        className="hud-btn hud-btn-primary flex items-center gap-2"
                        data-testid="import-commit"
                      >
                        IMPORT {okCount} {okCount === 1 ? "ROW" : "ROWS"}
                      </button>
                    </div>
                  </div>

                  {sourceHeaders.length > 0 && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setShowMap((s) => !s)}
                        className={`hud-btn !px-2 !py-1 ${showMap ? "hud-btn-primary" : ""}`}
                        data-testid="import-map-toggle"
                      >
                        MAP COLUMNS
                      </button>
                      <span className="mono text-[10px] text-muted-foreground tracking-widest uppercase">
                        Match your CSV headers to the fields below if they didn’t line up.
                      </span>
                    </div>
                  )}

                  {showMap && (
                    <div className="hud-frame p-4 space-y-3" data-testid="import-map">
                      <div className="kpi-label">Column mapping</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                        {columns.map((c) => (
                          <label key={c} className="flex items-center justify-between gap-2 text-xs">
                            <span className="mono uppercase tracking-widest text-muted-foreground truncate">{c}</span>
                            <select
                              value={mapping[c] || ""}
                              onChange={(e) => setMapping((m) => ({ ...m, [c]: e.target.value }))}
                              className="hud-input !py-1 !w-40"
                              data-testid={`map-${c}`}
                            >
                              <option value="">— none —</option>
                              {sourceHeaders.map((h) => (
                                <option key={h} value={h}>{h}</option>
                              ))}
                            </select>
                          </label>
                        ))}
                      </div>
                      <div className="flex justify-end">
                        <button
                          onClick={applyMapping}
                          disabled={busy}
                          className="hud-btn hud-btn-primary"
                          data-testid="import-apply-map"
                        >
                          {busy ? "APPLYING…" : "APPLY MAPPING"}
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="hud-frame overflow-hidden">
                    <div className="overflow-x-auto max-h-[42vh] overflow-y-auto">
                      <table className="ox-table" style={{ width: "max-content", minWidth: "100%" }}>
                        <thead>
                          <tr>
                            <th style={{ width: 44 }} />
                            {columns.map((c) => (
                              <th key={c} className="whitespace-nowrap">{c}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((r) => (
                            <tr key={r.index} data-testid={`import-row-${r.index}`}
                              className={r.status === "error"
                                ? "bg-[hsl(var(--oxblood)/0.08)]"
                                : (r.duplicate ? "bg-amber-500/10" : "")}>
                              <td className="!text-center">
                                {r.status === "error" ? (
                                  <span title={(r.errors || []).join("\n")}>
                                    <AlertTriangle size={13} className="text-[hsl(var(--oxblood))] inline" />
                                  </span>
                                ) : r.duplicate ? (
                                  <span title={(r.warnings || []).join("\n") || "Possible duplicate"} data-testid={`import-dup-${r.index}`}>
                                    <AlertTriangle size={13} className="text-amber-500 inline" />
                                  </span>
                                ) : (
                                  <CheckCircle2 size={13} className="text-emerald-500 inline" />
                                )}
                              </td>
                              {columns.map((c) => (
                                <td key={c} className="whitespace-nowrap text-xs">
                                  {String((r.raw && r.raw[c] != null ? r.raw[c] : "") || "")}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {(errorCount > 0 || dupCount > 0) && (
                    <p className="mono text-[10px] text-muted-foreground tracking-widest uppercase">
                      {errorCount > 0 && "Red rows have formatting issues — use “Fix all with AI” to auto-repair dates, numbers and split fields. "}
                      {dupCount > 0 && "Amber rows look like duplicates of entries you already have; they’re skipped unless you untick “Skip duplicates”."}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
